import { Component } from '@theme/component';

/**
 * @typedef {{
 *   scroller: HTMLElement,
 *   track: HTMLElement,
 *   prev?: HTMLButtonElement,
 *   next?: HTMLButtonElement,
 *   scrollbar?: HTMLElement,
 *   scrollbarThumb?: HTMLElement,
 * }} Refs
 * @extends {Component<Refs>}
 */
export class FeedingBundleSetsComponent extends Component {
    /** @type {string[]} */
    requiredRefs = ['scroller', 'track'];

    #abortController = new AbortController();

    /** @type {boolean} */
    #isPointerScrolling = false;

    /** @type {boolean} */
    #didDrag = false;

    /** @type {number} */
    #pointerStartX = 0;

    /** @type {number} */
    #pointerStartScroll = 0;

    /** @type {number | null} */
    #pointerId = null;

    connectedCallback() {
        super.connectedCallback();
        this.#setupNavigation();
        this.#setupScrollerDrag();
        this.#setupScrollbar();
        this.#syncScrollerWidth();
        this.#updateChrome();

        const { signal } = this.#abortController;
        const resizeObserver = new ResizeObserver(() => {
            this.#syncScrollerWidth();
            this.#updateChrome();
        });
        resizeObserver.observe(this.refs.scroller);
        signal.addEventListener('abort', () => resizeObserver.disconnect());
        this.refs.scroller.addEventListener('scroll', () => this.#updateChrome(), { signal, passive: true });
        this.refs.scroller.addEventListener(
            'keydown',
            (event) => {
                if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    this.#scrollBy(1);
                } else if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    this.#scrollBy(-1);
                }
            },
            { signal }
        );
        window.addEventListener('resize', () => this.#updateChrome(), { signal, passive: true });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
    }

    updatedCallback() {
        this.#updateChrome();
    }

    #setupNavigation() {
        const { signal } = this.#abortController;
        this.refs.prev?.addEventListener('click', () => this.#scrollBy(-1), { signal });
        this.refs.next?.addEventListener('click', () => this.#scrollBy(1), { signal });
    }

    #setupScrollerDrag() {
        const { scroller } = this.refs;
        const { signal } = this.#abortController;

        scroller.addEventListener(
            'pointerdown',
            (event) => {
                if (event.pointerType !== 'mouse' || event.button !== 0) return;
                if (event.target instanceof Element && event.target.closest('button')) return;

                this.#isPointerScrolling = true;
                this.#didDrag = false;
                this.#pointerStartX = event.clientX;
                this.#pointerStartScroll = scroller.scrollLeft;
                this.#pointerId = event.pointerId;
            },
            { signal }
        );

        scroller.addEventListener(
            'pointermove',
            (event) => {
                if (!this.#isPointerScrolling) return;
                const delta = event.clientX - this.#pointerStartX;
                if (!this.#didDrag && Math.abs(delta) < 6) return;

                if (!this.#didDrag) {
                    this.#didDrag = true;
                    scroller.classList.add('is-dragging');
                    if (this.#pointerId != null) {
                        scroller.setPointerCapture(this.#pointerId);
                    }
                }
                scroller.scrollLeft = this.#pointerStartScroll - delta;
            },
            { signal }
        );

        scroller.addEventListener(
            'click',
            (event) => {
                if (!this.#didDrag) return;
                event.preventDefault();
                event.stopPropagation();
            },
            { signal, capture: true }
        );

        scroller.addEventListener('pointerup', () => this.#endPointerScroll(), { signal });
        scroller.addEventListener('pointercancel', () => this.#endPointerScroll(), { signal });
    }

    #endPointerScroll() {
        const { scroller } = this.refs;
        if (this.#pointerId != null && scroller.hasPointerCapture?.(this.#pointerId)) {
            scroller.releasePointerCapture(this.#pointerId);
        }
        this.#pointerId = null;
        this.#isPointerScrolling = false;
        scroller.classList.remove('is-dragging');
        this.#updateChrome();
        requestAnimationFrame(() => {
            this.#didDrag = false;
        });
    }

    #setupScrollbar() {
        const { scrollbar, scrollbarThumb, scroller } = this.refs;
        if (!scrollbar || !scrollbarThumb) return;

        const { signal } = this.#abortController;
        let dragging = false;
        let startX = 0;
        let startScroll = 0;

        scrollbarThumb.addEventListener(
            'pointerdown',
            (event) => {
                if (event.button !== 0) return;
                dragging = true;
                startX = event.clientX;
                startScroll = scroller.scrollLeft;
                scrollbarThumb.setPointerCapture(event.pointerId);
                event.preventDefault();
            },
            { signal }
        );

        scrollbarThumb.addEventListener(
            'pointermove',
            (event) => {
                if (!dragging) return;
                const maxScroll = this.#maxScroll();
                if (maxScroll <= 0) return;

                const trackWidth = scrollbar.clientWidth;
                const thumbWidth = scrollbarThumb.offsetWidth;
                const travel = Math.max(trackWidth - thumbWidth, 1);
                scroller.scrollLeft = startScroll + ((event.clientX - startX) * maxScroll) / travel;
            },
            { signal }
        );

        const stopDrag = () => {
            dragging = false;
        };

        scrollbarThumb.addEventListener('pointerup', stopDrag, { signal });
        scrollbarThumb.addEventListener('pointercancel', stopDrag, { signal });

        scrollbar.addEventListener(
            'pointerdown',
            (event) => {
                if (event.target === scrollbarThumb) return;
                const maxScroll = this.#maxScroll();
                if (maxScroll <= 0) return;

                const rect = scrollbar.getBoundingClientRect();
                const ratio = (event.clientX - rect.left) / rect.width;
                scroller.scrollLeft = ratio * maxScroll - scroller.clientWidth / 2;
            },
            { signal }
        );
    }

    /**
     * @param {number} direction
     */
    #scrollBy(direction) {
        const slide = /** @type {HTMLElement | null} */ (
            this.refs.track.querySelector('.na-feeding-bundle-sets__slide')
        );
        if (!slide) return;

        const gap = parseFloat(getComputedStyle(this.refs.track).gap) || 0;
        this.refs.scroller.scrollBy({ left: direction * (slide.offsetWidth + gap), behavior: 'smooth' });
    }

    #maxScroll() {
        return this.refs.scroller.scrollWidth - this.refs.scroller.clientWidth;
    }

    #syncScrollerWidth() {
        this.style.setProperty('--na-fbs-scroller-width', `${this.refs.scroller.clientWidth}px`);
    }

    #updateChrome() {
        this.#updateNavigationState();
        this.#updateScrollbar();
    }

    #updateNavigationState() {
        const { prev, next } = this.refs;
        if (!prev || !next) return;

        const maxScroll = this.#maxScroll();
        const atStart = this.refs.scroller.scrollLeft <= 1;
        const atEnd = this.refs.scroller.scrollLeft >= maxScroll - 1;

        prev.disabled = atStart;
        next.disabled = atEnd || maxScroll <= 0;
        prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
        next.setAttribute('aria-disabled', atEnd || maxScroll <= 0 ? 'true' : 'false');
    }

    #updateScrollbar() {
        const { scrollbar, scrollbarThumb, scroller } = this.refs;
        if (!scrollbar || !scrollbarThumb) return;

        const total = scroller.scrollWidth;
        const visible = scroller.clientWidth;
        const trackWidth = scrollbar.clientWidth;
        const maxScroll = Math.max(total - visible, 0);
        const thumbWidth = total > 0 ? Math.min(Math.max((visible / total) * trackWidth, 48), trackWidth) : trackWidth;
        const left = maxScroll > 0 ? (scroller.scrollLeft / maxScroll) * (trackWidth - thumbWidth) : 0;

        scrollbarThumb.style.width = `${thumbWidth}px`;
        scrollbarThumb.style.left = `${left}px`;
    }
}

if (!customElements.get('feeding-bundle-sets-component')) {
    customElements.define('feeding-bundle-sets-component', FeedingBundleSetsComponent);
}
